/**
 * Phase 0 · Infra-1 · P0.CLI.4
 *
 * MM:SS call timer. Starts from `startedAt` and re-renders every second.
 */

import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet } from 'react-native';

interface CallTimerProps {
  startedAt: number | null;
  status?: string;
}

function format(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

export default function CallTimer({ startedAt, status }: CallTimerProps) {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (startedAt === null) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [startedAt]);

  const elapsed = startedAt === null ? 0 : now - startedAt;

  return (
    <View style={styles.pill}>
      {status ? <Text style={styles.status}>{status}</Text> : null}
      <Text style={styles.time}>{format(elapsed)}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  // Figma node 6686:19987 — solid Bunker pill, radius 52, padding 4×8.
  // White Inter-500 14/140% so the timer reads cleanly against both the
  // Blue Whale waiting background and a remote video.
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 52,
    backgroundColor: '#151515',
    alignSelf: 'center',
  },
  status: {
    color: '#FFFFFF',
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 19.6,
  },
  time: {
    color: '#FFFFFF',
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 19.6,
    fontVariant: ['tabular-nums'],
  },
});
